import { useCallback, useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { Box } from '@mui/material';

interface SignatureCanvasProps {
  onChange: (dataUrl: string | null) => void;
  ariaLabel: string;
  disabled?: boolean;
}

const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 280;

export function SignatureCanvas({ onChange, ariaLabel, disabled = false }: SignatureCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const hasStroke = useRef(false);

  const emit = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !hasStroke.current) {
      onChange(null);
      return;
    }
    onChange(canvas.toDataURL('image/png'));
  }, [onChange]);

  const context = () => canvasRef.current?.getContext('2d') ?? null;

  const position = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    const ctx = context();
    const point = position(event);
    drawing.current = true;
    hasStroke.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(point.x, point.y);
    }
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || disabled) return;
    const ctx = context();
    const point = position(event);
    if (ctx) {
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
    }
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    drawing.current = false;
    event.currentTarget.releasePointerCapture(event.pointerId);
    emit();
  };

  useEffect(() => {
    const ctx = context();
    if (!ctx) return;
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111'; // design-allow-hex: signature ink on the white canvas, same in every theme and in the evidence PDF
  }, []);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        aria-label={ariaLabel}
        data-testid="signature-canvas"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          display: 'block',
          width: '100%',
          height: 180,
          touchAction: 'none',
          cursor: disabled ? 'default' : 'crosshair',
        }}
      />
    </Box>
  );
}
